import { useEffect, useId, useState } from "react";
import { Pause, Square } from "lucide-react";

const MIN_COMMENT = 3;
const MAX_COMMENT = 2000;

export default function CommentDialog({
  title,
  description,
  confirmLabel,
  tone = "pause",
  required = true,
  busy = false,
  onCancel,
  onConfirm,
}) {
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState("");
  const headingId = useId();
  const trimmed = comment.trim();
  const tooShort = required && trimmed.length < MIN_COMMENT;
  const tooLong = trimmed.length > MAX_COMMENT;
  const canSubmit = !busy && !tooShort && !tooLong;
  const Icon = tone === "stop" ? Square : Pause;

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitError("");
    try {
      await onConfirm(trimmed);
    } catch (err) {
      setSubmitError(err?.message || "Unable to save");
    }
  };

  return (
    <div className="pat-dialog-backdrop" role="presentation" onMouseDown={busy ? undefined : onCancel}>
      <form
        className={`pat-dialog pat-dialog--${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="pat-dialog__header">
          <span className="pat-dialog__icon" aria-hidden="true">
            <Icon size={16} />
          </span>
          <div>
            <h2 id={headingId} className="pat-dialog__title">
              {title}
            </h2>
            {description ? <p className="pat-dialog__copy">{description}</p> : null}
          </div>
        </div>
        <label className="pat-dialog__label" htmlFor={`${headingId}-comment`}>
          Reason / comments
        </label>
        <textarea
          id={`${headingId}-comment`}
          className="pat-dialog__textarea"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          maxLength={MAX_COMMENT}
          rows={4}
          autoFocus
          disabled={busy}
          placeholder="Enter a brief reason..."
        />
        <div className="pat-dialog__meta">
          <span>
            {required ? `${MIN_COMMENT}–${MAX_COMMENT} characters` : `Up to ${MAX_COMMENT} characters`}
          </span>
          <span>{trimmed.length}/{MAX_COMMENT}</span>
        </div>
        {submitError ? <p className="pat-dialog__error">{submitError}</p> : null}
        <div className="pat-dialog__actions">
          <button type="button" className="pat-dialog__cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className={`pat-dialog__confirm pat-dialog__confirm--${tone}`}
            disabled={!canSubmit}
          >
            {busy ? "Saving…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
